
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';
import { Database } from '@/integrations/supabase/types';

// Type for line items as stored in the estimate_line_items table
type EstimateLineItemRow = Database['public']['Tables']['estimate_line_items']['Row'] & {
  supplier_categories?: {
    id: string;
    name: string;
  } | null;
};

export const useEstimateLineItems = (estimateId: string) => {
  const { profile } = useAuth();

  // Fetch estimate details
  const { data: estimate, isLoading: isLoadingEstimate, error: estimateError } = useQuery({
    queryKey: ['estimate', estimateId],
    queryFn: async () => {
      console.log('[useEstimateLineItems] Fetching estimate with ID:', estimateId);
      const { data, error } = await supabase
        .from('estimates')
        .select('*')
        .eq('id', estimateId)
        .single();

      if (error) {
        console.error('[useEstimateLineItems] Error fetching estimate:', error);
        throw error;
      }
      
      console.log('[useEstimateLineItems] Estimate data loaded:', data);
      return data;
    },
    enabled: !!estimateId,
  });

  // Fetch existing line items from table (used for draft estimates)
  const { data: lineItems, isLoading: isLoadingLineItems, error: lineItemsError } = useQuery({
    queryKey: ['estimateLineItems', estimateId],
    queryFn: async () => {
      console.log('[useEstimateLineItems] Fetching line items for estimate:', estimateId);
      const { data, error } = await supabase
        .from('estimate_line_items')
        .select(`
          *,
          supplier_categories!category_id(
            id,
            name
          )
        `)
        .eq('estimate_id', estimateId)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('[useEstimateLineItems] Error fetching line items:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }
      
      console.log('[useEstimateLineItems] Line items loaded:', data);
      return data || [];
    },
    enabled: !!estimateId,
    retry: false,
  });

  // Fetch version snapshot for non-draft estimates
  const { data: versionSnapshot, isLoading: isLoadingVersionSnapshot, error: versionSnapshotError } = useQuery({
    queryKey: ['estimateVersionSnapshot', estimateId, estimate?.main_version_id],
    queryFn: async () => {
      if (!estimate?.main_version_id) {
        console.log('[useEstimateLineItems] No main_version_id, skipping version snapshot fetch');
        return null;
      }
      
      console.log('[useEstimateLineItems] Fetching version snapshot for version:', estimate.main_version_id);
      const { data, error } = await supabase
        .from('estimate_versions')
        .select('id, version_number, version_type, status, line_items_snapshot, sent_date, accepted_date')
        .eq('id', estimate.main_version_id)
        .single();

      if (error) {
        console.error('[useEstimateLineItems] Error fetching version snapshot:', error);
        throw error;
      }
      
      console.log('[useEstimateLineItems] Version snapshot loaded:', {
        versionId: data?.id,
        versionNumber: data?.version_number,
        status: data?.status,
        lineItemsCount: Array.isArray(data?.line_items_snapshot) ? data.line_items_snapshot.length : 0
      });
      return data;
    },
    enabled: !!estimate?.main_version_id && estimate?.status !== 'draft',
  });

  // Determine effective line items based on estimate status
  // Draft estimates use live table data, non-draft use version snapshot
  // Returns undefined when data is still loading to prevent premature initialization
  const effectiveLineItems = useMemo((): EstimateLineItemRow[] | undefined => {
    if (estimate?.status === 'draft') {
      console.log('[useEstimateLineItems] Using live table line items for draft estimate');
      return lineItems; // Return undefined if still loading
    }
    
    // For non-draft estimates, we MUST have the version snapshot loaded
    // Return undefined to signal "not ready" - this prevents race conditions
    if (isLoadingVersionSnapshot) {
      console.log('[useEstimateLineItems] Version snapshot still loading, returning undefined');
      return undefined;
    }
    
    // For sent/approved/rejected/expired/cancelled, use the version snapshot
    if (versionSnapshot?.line_items_snapshot && Array.isArray(versionSnapshot.line_items_snapshot)) {
      console.log('[useEstimateLineItems] Using version snapshot line items for non-draft estimate:', {
        status: estimate?.status,
        itemCount: versionSnapshot.line_items_snapshot.length
      });
      // Cast the JSON snapshot to the correct type - it should match the line item structure
      return versionSnapshot.line_items_snapshot as unknown as EstimateLineItemRow[];
    }
    
    // If no version snapshot and not loading, return empty array (edge case)
    console.warn('[useEstimateLineItems] No version snapshot available for non-draft estimate');
    return [];
  }, [estimate?.status, lineItems, versionSnapshot, isLoadingVersionSnapshot]);

  // Fetch available internal line items with markup data
  const { data: internalLineItems, isLoading: isLoadingInternal, error: internalError } = useQuery({
    queryKey: ['internalLineItems', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) {
        console.warn('[useEstimateLineItems] No company ID available in profile:', profile);
        return [];
      }
      
      console.log('[useEstimateLineItems] Fetching internal line items for company:', profile.company_id);
      const { data, error } = await supabase
        .from('internal_line_items')
        .select('*')
        .eq('company_id', profile.company_id)
        .eq('is_active', true)
        .order('title', { ascending: true });

      if (error) {
        console.error('[useEstimateLineItems] Error fetching internal line items:', error);
        throw error;
      }
      
      console.log('[useEstimateLineItems] Internal line items loaded:', data);
      return data || [];
    },
    enabled: !!profile?.company_id,
  });

  // Fetch available supplier line items with markup data and custom tiers
  const { data: supplierLineItems, isLoading: isLoadingSupplier, error: supplierError } = useQuery({
    queryKey: ['supplierLineItems', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) {
        console.warn('[useEstimateLineItems] No company ID available for supplier items:', profile);
        return [];
      }
      
      console.log('[useEstimateLineItems] Fetching supplier line items for company:', profile.company_id);
      const { data, error } = await supabase
        .from('internal_line_items')
        .select(`
          *,
          suppliers!supplier_id(
            id,
            name
          )
        `)
        .eq('company_id', profile.company_id)
        .not('supplier_id', 'is', null)
        .eq('is_active', true)
        .order('title', { ascending: true });

      if (error) {
        console.error('[useEstimateLineItems] Error fetching supplier line items:', error);
        throw error;
      }
      
      console.log('[useEstimateLineItems] Supplier line items loaded:', data);
      return data || [];
    },
    enabled: !!profile?.company_id,
  });

  // Fetch available assemblies
  const { data: assemblies, isLoading: isLoadingAssemblies, error: assembliesError } = useQuery({
    queryKey: ['assemblies', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) {
        console.warn('[useEstimateLineItems] No company ID available for assemblies:', profile);
        return [];
      }
      
      console.log('[useEstimateLineItems] Fetching assemblies for company:', profile.company_id);
      const { data, error } = await supabase
        .from('assemblies')
        .select('*')
        .or(`company_id.eq.${profile.company_id},is_global.eq.true`)
        .order('name', { ascending: true });

      if (error) {
        console.error('[useEstimateLineItems] Error fetching assemblies:', error);
        throw error;
      }
      
      console.log('[useEstimateLineItems] Assemblies loaded:', data);
      return data || [];
    },
    enabled: !!profile?.company_id,
  });

  const isLoading = isLoadingEstimate || isLoadingLineItems || isLoadingInternal || isLoadingSupplier || isLoadingAssemblies || 
    (estimate?.status !== 'draft' && isLoadingVersionSnapshot);

  return {
    estimate,
    lineItems: effectiveLineItems,
    versionSnapshot,
    internalLineItems,
    supplierLineItems,
    assemblies,
    isLoading,
    errors: {
      estimateError,
      lineItemsError,
      versionSnapshotError,
      internalError,
      supplierError,
      assembliesError,
    },
  };
};
